// Cobertura declarada por el operador autenticado (`logisticscompanies.coverageAreas`),
// para las vistas que NO reciben el documento de la empresa por prop.
//
// /admin/settings ya lo tiene cargado y se lo pasa a sus secciones; las vistas de
// facturación (p. ej. el override de tarifas por repartidor) viven en otro árbol y
// tendrían que pedirlo. Este hook centraliza esa lectura con un cache de módulo para que
// abrir el modal varias veces no dispare una request por vez.
import { useEffect, useState } from "react";
import useAuth from "./useAuth";
import { getLogisticsCompany } from "../api/settings";

// logisticCompanyId -> Promise<coverageAreas[] | null>. `null` = no se pudo leer.
const coveragePromises = new Map();

/** Desenvuelve el doc: `getLogisticsCompany` entrega la respuesta axios completa. */
const unwrap = (payload) => {
  const doc = payload?.data ?? payload;
  return doc && typeof doc === "object" ? doc : null;
};

function loadCoverage(companyId) {
  if (!coveragePromises.has(companyId)) {
    coveragePromises.set(
      companyId,
      getLogisticsCompany(companyId)
        .then((res) => {
          const doc = unwrap(res);
          if (!doc) return null;
          return Array.isArray(doc.coverageAreas) ? doc.coverageAreas : [];
        })
        .catch((err) => {
          // Se limpia para que un fallo de red no deje el cache envenenado toda la sesión.
          coveragePromises.delete(companyId);
          console.warn("useOperatorCoverage: no se pudo leer la cobertura", err);
          return null;
        }),
    );
  }
  return coveragePromises.get(companyId);
}

/**
 * @returns {{ coverageAreas: Array|null, loading: boolean }}
 *
 * `coverageAreas === null` significa "no se sabe" (aún cargando o falló la lectura), y es
 * DISTINTO de `[]` ("el operador no declaró cobertura"). Los consumidores tienen que
 * tratarlos distinto: ante `null` no se puede afirmar que una comuna esté fuera de
 * cobertura, y marcar reglas de dinero como inválidas por un error de red sería peor que
 * no marcar nada.
 */
export default function useOperatorCoverage() {
  const auth = useAuth();
  const user = auth?.user ?? null;
  const companyId = user?.logisticCompany?._id ?? user?.logisticCompanyId ?? null;

  const [state, setState] = useState({ coverageAreas: null, loading: !!companyId });

  useEffect(() => {
    if (!companyId) {
      setState({ coverageAreas: null, loading: false });
      return undefined;
    }
    let alive = true;
    setState({ coverageAreas: null, loading: true });
    loadCoverage(String(companyId)).then((areas) => {
      if (alive) setState({ coverageAreas: areas, loading: false });
    });
    return () => {
      alive = false;
    };
  }, [companyId]);

  return state;
}
